<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
  <div class="map-container" @click="isActiveImage = true" ref="container">
    <div class="map-container-header">
      <button class="delete close" type="button" :aria-label="$t('button-close')" @click="$emit('close')"></button>
    </div>
    <template v-if="!loading && zoom !== null">
      <viewer-image-map
        ref="imageMap"
        :active-draw-interaction="activeDrawInteraction"
        :active-modify-interaction="activeModifyInteraction"
        :active-select-interaction="activeSelectInteraction"
        :base-layer-url="baseLayerURL"
        :center.sync="center"
        :document="document"
        :extent="extent"
        :heatmap-overlay="heatmapOverlay"
        :malaria-detection-overlay="malariaDetectionOverlay"
        :malaria-heatmap="malariaHeatmap"
        :image="image"
        :image-size="imageSize"
        :index="index"
        :max-zoom="maxZoom"
        :projection-name="projectionName"
        :rotation.sync="rotation"
        :selected-layers="selectedLayers"
        :tile-load-function="tileLoadFunction"
        :tile-size="tileSize"
        :zoom.sync="zoom"
        @base-layer-mounted="bindOverviewViewport"
        @map-mounted="updateKeyboardInteractions"
        @pointermove="projectedMousePosition = $event"
        @view-mounted="viewMounted"
      />
      <viewer-image-overlays
        :image="image"
        :index="index"
        :overview-image-url="overviewImageUrl"
        :overview-style="overviewStyle"
        :overview-viewport="overviewViewport"
        :projected-mouse-position="projectedMousePosition"
        :show-add-ontology-modal="showAddOntologyModal"
        :zoom="zoom"
        @center-view="centerViewOnAnnot"
        @close-add-ontology-modal="showAddOntologyModal = false"
        @fit-zoom="fitZoom"
        @overview-center="centerOverviewOnCoordinate"
        @set-magnification="setMagnification"
        @take-screenshot="takeScreenshot"
      />
    </template>
  </div>
</template>
<script>
import { get } from '@/utils/store-helpers';
import ViewerImageMap from './ViewerImageMap';
import ViewerImageOverlays from './ViewerImageOverlays';
import { addProj, createProj, getProj } from 'vuelayers/lib/ol-ext';
import { KeyboardPan, KeyboardZoom } from 'ol/interaction';
import { noModifierKeys, targetNotEditable } from 'ol/events/condition';
import WKT from 'ol/format/WKT';
import { unByKey } from 'ol/Observable';
import { Cytomine, ImageConsultation, Annotation, SliceInstance } from '@/api';
import { appendShortTermToken } from '@/utils/token-utils';
import { DEFAULT_MAGNIFICATION, getZoomForMagnification } from './magnifications';

const OVERVIEW_MAX_SIZE = 300;

export default {
  name: 'cytomine-image',
  props: {
    command: Object,
    index: String
  },
  components: {
    ViewerImageMap,
    ViewerImageOverlays
  },
  data() {
    return {
      projectedMousePosition: [0, 0],
      routedAnnotation: null,
      loading: true,
      overviewListeners: [],
      overviewViewport: null,
      format: new WKT(),
      showAddOntologyModal: false
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    document() {
      return document;
    },
    project: get('currentProject/project'),
    currentUser: get('currentUser/user'),
    routedAction() {
      return this.$route.query.action;
    },
    configUI: get('currentProject/configUI'),
    viewerModule() {
      return this.$store.getters['currentProject/viewerModulePath'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModulePath'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    nbImages() {
      return Object.keys(this.viewerWrapper.images).length;
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    canEdit() {
      return this.$store.getters['currentProject/canEditImage'](this.image);
    },
    projectionName() {
      return `CYTO-${this.image.id}`;
    },
    selectedLayers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },
    heatmapOverlay() {
      return this.imageWrapper.heatmapOverlay;
    },
    malariaHeatmap() {
      return this.imageWrapper.malariaHeatmap;
    },
    malariaDetectionOverlay() {
      return this.imageWrapper.malariaDetectionOverlay;
    },
    heatmapMockEnabled() {
      return ['1', 'true', 'yes'].includes(String(this.$route.query.heatmapMock || '').toLowerCase());
    },
    isActiveImage: {
      get() {
        return this.viewerWrapper.activeImage === this.index;
      },
      set(value) {
        if (value) {
          if (this.viewerWrapper) {
            this.$store.commit(this.viewerModule + 'setActiveImage', this.index);
          }
        } else {
          throw new Error('Cannot unset active map');
        }
      }
    },
    activePanel() {
      return this.imageWrapper.activePanel;
    },
    activeTool() {
      return this.imageWrapper.draw.activeTool;
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    maxZoom() {
      return this.$store.getters[this.imageModule + 'maxZoom'];
    },
    center: {
      get() {
        return this.imageWrapper.view.center;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setCenter', { index: this.index, center: value });
      }
    },
    zoom: {
      get() {
        return this.imageWrapper.view.zoom;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setZoom', { index: this.index, zoom: Number(value) });
      }
    },
    rotation: {
      get() {
        return this.imageWrapper.view.rotation;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setRotation', { index: this.index, rotation: Number(value) });
      }
    },
    extent() {
      return [0, 0, this.image.width, this.image.height];
    },
    imageSize() {
      return [this.image.width, this.image.height];
    },
    tileSize() {
      return this.image.tileSize;
    },
    baseLayerProcessingParams() {
      return this.$store.getters[this.imageModule + 'tileRequestParams'];
    },
    baseLayerSliceParams() {
      return {
        zSlices: this.slices[0].zStack,
        timepoints: this.slices[0].time
      };
    },
    baseLayerURLQuery() {
      let query = new URLSearchParams({ ...this.baseLayerSliceParams, ...this.baseLayerProcessingParams }).toString();
      if (query.length > 0) {
        return `?${query}`;
      }
      return query;
    },
    baseLayerURL() {
      let slice = this.slices[0];
      return Cytomine.instance.host + Cytomine.instance.basePath + `sliceinstance/${slice.id}/normalized-tile/zoom/{z}/tx/{x}/ty/{y}.jpg${this.baseLayerURLQuery}`;
    },
    overviewImageUrl() {
      let url = this.image.previewURL?.(300, 'jpg') || this.image.thumbURL?.(300, 'jpg');
      if (!url) {
        url = this.overviewTileUrl;
      }
      return appendShortTermToken(url, this.shortTermToken);
    },
    overviewTileUrl() {
      return this.baseLayerURL
        .replace('{z}', '0')
        .replace('{x}', '0')
        .replace('{y}', '0');
    },
    tileLoadFunction() {
      return (tile, src) => {
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        // 检查是否为临时访问令牌用户
        if (this.$keycloak && this.$keycloak.hasTemporaryToken) {
          // 从URL中提取access_token并添加到请求URL中
          const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
          const accessToken = urlParams.get('access_token');
          if (accessToken) {
            const separator = src.includes('?') ? '&' : '?';
            const urlWithToken = `${src}${separator}access_token=${accessToken}`;
            xhr.open('GET', urlWithToken);
          } else {
            xhr.open('GET', src);
          }
        } else {
          // 正常用户使用shortTermToken
          xhr.open('GET', src);
          xhr.setRequestHeader('Authorization', 'Bearer ' + this.shortTermToken);
        }
        xhr.addEventListener('load', () => {
          const url = URL.createObjectURL(xhr.response);
          const tileImage = tile.getImage();
          tileImage.addEventListener('load', () => URL.revokeObjectURL(url));
          tileImage.src = url;
        });
        xhr.send();
      };
    },
    correction() {
      return ['correct-add', 'correct-remove'].includes(this.activeEditTool);
    },
    activeSelectInteraction() {
      return this.activeTool === 'select';
    },
    activeDrawInteraction() {
      return !this.activeSelectInteraction || this.correction;
    },
    activeModifyInteraction() {
      return this.activeSelectInteraction && this.activeEditTool && !this.correction;
    },
    idealZoom() {
      let container = this.$refs.container;
      let idealZoom = this.maxZoom;
      let factor = this.maxZoom - this.image.zoom;
      let mapWidth = this.image.width * Math.pow(2, factor);
      let mapHeight = this.image.height * Math.pow(2, factor);
      while (mapWidth > container.clientWidth || mapHeight > container.clientHeight) {
        mapWidth /= 2;
        mapHeight /= 2;
        idealZoom--;
      }
      return idealZoom;
    },
    overviewStyle() {
      if (!this.image || !this.image.width || !this.image.height) {
        return {};
      }

      const ratio = this.image.width / this.image.height;

      let width, height;
      if (ratio > 1) { // 横向较长
        width = OVERVIEW_MAX_SIZE;
        height = OVERVIEW_MAX_SIZE / ratio;
      }
      else { // 纵向较长
        height = OVERVIEW_MAX_SIZE;
        width = OVERVIEW_MAX_SIZE * ratio;
      }

      return {
        width: `${width}px`,
        height: `${height}px`,
      };
    }
  },
  watch: {
    command(command) {
      if (command) {
        this.runImageCommand(command.name);
      }
    },
    heatmapMockEnabled(value) {
      if (value) {
        this.applyMockHeatmapOverlay();
      } else {
        this.$store.dispatch(this.imageModule + 'clearHeatmapOverlay');
      }
    },
    overviewStyle() {
      this.$nextTick(this.updateOverviewViewport);
    }
  },
  methods: {
    runImageCommand(commandName) {
      const commands = {
        fitZoom: this.fitZoom,
        resetZoom: this.resetZoom,
        takeScreenshot: this.takeScreenshot,
        zoomIn: this.zoomIn,
        zoomOut: this.zoomOut
      };
      if (commands[commandName]) {
        commands[commandName]();
      }
    },
    async zoomIn() {
      const view = await this.$refs.imageMap.getViewComponent();
      view.animate({ zoom: this.zoom + 1, duration: 250 });
    },
    async zoomOut() {
      const view = await this.$refs.imageMap.getViewComponent();
      view.animate({ zoom: this.zoom - 1, duration: 250 });
    },
    async resetZoom() {
      const view = await this.$refs.imageMap.getViewComponent();
      view.animate({ zoom: this.image.zoom, duration: 250 });
    },
    setMagnification(mag) {
      let targetZoom = getZoomForMagnification(this.image, mag);
      if (targetZoom === null) return;
      this.$refs.imageMap.getViewComponent().then(view => {
        view.animate({ zoom: targetZoom, duration: 500 });
      });
    },
    setInitialZoom() {
      if (this.zoom !== null) {
        return; // not the first time the viewer is opened => zoom was already initialized
      }
      this.zoom = getZoomForMagnification(this.image, DEFAULT_MAGNIFICATION, this.idealZoom);
    },
    async updateMapSize() {
      await this.$nextTick();
      if (this.$refs.imageMap) {
        this.$refs.imageMap.updateSize();
        this.updateOverviewViewport();
      }
    },
    async updateKeyboardInteractions() {
      const map = await this.$refs.imageMap.getMap();
      map.getInteractions().forEach(interaction => {
        if (interaction instanceof KeyboardPan || interaction instanceof KeyboardZoom) {
          interaction.condition_ = (mapBrowserEvent) => {
            return noModifierKeys(mapBrowserEvent)
              && targetNotEditable(mapBrowserEvent)
              && this.isActiveImage
              && !mapBrowserEvent.originalEvent.target.classList.contains('ql-editor');
          };
        }
      });
    },
    async viewMounted() {
      await this.$refs.imageMap.getViewComponent();
      if (this.routedAnnotation) {
        this.centerViewOnAnnot(this.routedAnnotation, 500);
      }
    },
    async bindOverviewViewport() {
      let map = await this.$refs.imageMap.getMap();
      let view = map.getView();
      this.clearOverviewViewportListeners();
      this.overviewListeners = [
        view.on('change:center', this.updateOverviewViewport),
        view.on('change:resolution', this.updateOverviewViewport),
        view.on('change:rotation', this.updateOverviewViewport),
        map.on('change:size', this.updateOverviewViewport),
      ];
      this.updateOverviewViewport();
    },
    clearOverviewViewportListeners() {
      this.overviewListeners.forEach(unByKey);
      this.overviewListeners = [];
    },
    async centerOverviewOnCoordinate(coordinate) {
      let view = await this.$refs.imageMap.getViewComponent();
      view.animate({ center: coordinate, duration: 0 });
    },
    async updateOverviewViewport() {
      if (!this.$refs.imageMap || !this.image || !this.image.width || !this.image.height) {
        return;
      }
      let map = await this.$refs.imageMap.getMap();
      let size = map.getSize();
      if (!size || size[0] === 0 || size[1] === 0) {
        return;
      }

      this.overviewViewport = this.toOverviewViewportStyle(map.getView().calculateExtent(size));
    },
    toOverviewViewportStyle(extent) {
      let minX = Math.max(0, extent[0]);
      let minY = Math.max(0, extent[1]);
      let maxX = Math.min(this.image.width, extent[2]);
      let maxY = Math.min(this.image.height, extent[3]);
      let width = Math.max(0, maxX - minX);
      let height = Math.max(0, maxY - minY);

      return {
        left: `${(minX / this.image.width) * 100}%`,
        top: `${((this.image.height - maxY) / this.image.height) * 100}%`,
        width: `${(width / this.image.width) * 100}%`,
        height: `${(height / this.image.height) * 100}%`,
      };
    },
    togglePanel(panel) {
      this.$store.commit(this.imageModule + 'togglePanel', panel);
    },
    async fitZoom() {
      const view = await this.$refs.imageMap.getViewComponent();
      view.fit(this.extent, {
        duration: 250,
        constrainResolution: false
      });
    },
    async centerViewOnAnnot(annot, duration) {
      if (annot.image === this.image.id) {
        if (!annot.location) {
          //in case annotation location has not been loaded
          annot = (await Cytomine.instance.api.get(`/annotations/${annot.id}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        }
        if (annot.project !== this.project.id) {
          await this.$router.push(`/project/${annot.project}/image/${annot.image}/annotation/${annot.id}`);
        }
        let geometry = this.format.readGeometry(annot.location);
        const view = await this.$refs.imageMap.getViewComponent();
        await view.fit(geometry, { duration, padding: [10, 10, 10, 10], maxZoom: this.image.zoom });
        if (!Object.prototype.hasOwnProperty.call(annot, 'centroid')) {
          return;
        }
        // HACK: center set by view.fit() is incorrect => reset it manually
        this.center = (geometry.getType() === 'Point') ? geometry.getFirstCoordinate()
          : [annot.centroid.x, annot.centroid.y];
        // ---
      }
    },
    async selectAnnotationHandler({ index, annot, center = false, showComments = false, fromAuditLog = false }) {
      if (this.index === index && annot.image === this.image.id) {
        try {
          let sliceChange = false;
          if (!annot.slice) {
            //in case annotation slice has not been loaded
            annot = await Annotation.fetch(annot.id);
          }
          if (!this.sliceIds.includes(annot.slice)) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
            this.$eventBus.$emit('reloadAnnotations', { idImage: this.image.id, hard: true });
            sliceChange = true;
          }
          if (showComments) {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
          this.$eventBus.$emit('selectAnnotationInLayer', { index, annot });
          if (center) {
            await this.viewMounted();
            let duration = (sliceChange) ? undefined : 500;
            this.centerViewOnAnnot(annot, duration);
          }
        } catch (error) {
          console.log(error);
          this.$notify({
            type: 'error',
            text: this.$t(fromAuditLog ? 'notif-error-target-annotation-deleted' : 'notif-error-target-annotation')
          });
        }
      }
    },
    isPanelDisplayed(panel) {
      return this.configUI[`project-explore-${panel}`];
    },
    closeMetadataHandler() {
      if (this.isActiveImage) {
        this.$store.commit(this.imageModule + 'togglePanel', 'info');
      }
    },
    openAddOntologyModalHandler() {
      if (this.isActiveImage) {
        this.showAddOntologyModal = true;
      }
    },
    shortkeyHandler(key) {
      if (!key.startsWith('toggle-all-') && !this.isActiveImage) { // shortkey should only be applied to active map
        return;
      }
      key = key.replace('toggle-all-', 'toggle-');
      switch (key) {
        case 'toggle-information':
          if (this.isPanelDisplayed('info')) {
            this.togglePanel('info');
          }
          return;
        case 'toggle-zoom':
          if (this.isPanelDisplayed('digital-zoom')) {
            this.togglePanel('digital-zoom');
          }
          return;
        case 'toggle-link':
          if (this.isPanelDisplayed('link') && this.nbImages > 1) {
            this.togglePanel('link');
          }
          return;
        case 'toggle-filters':
          if (this.isPanelDisplayed('color-manipulation')) {
            this.togglePanel('colors');
          }
          return;
        case 'toggle-layers':
          if (this.isPanelDisplayed('image-layers')) {
            this.togglePanel('layers');
          }
          return;
        case 'toggle-ontology':
          if (this.isPanelDisplayed('ontology')) {
            this.togglePanel('ontology');
          }
          return;
        case 'toggle-properties':
          if (this.isPanelDisplayed('property')) {
            this.togglePanel('properties');
          }
          return;
        case 'toggle-review':
          if (this.isPanelDisplayed('review') && this.canEdit) {
            this.togglePanel('review');
          }
          return;
      }
    },
    async takeScreenshot() {
      // Use of css percent values and html2canvas results in strange behavior
      // Set image container as actual height in pixel (not in percent) to avoid image distortion when retrieving canvas
      let container = this.$refs.container;
      let mapElement = container.querySelector('.ol-unselectable');
      let containerHeight = container.clientHeight;
      container.style.height = containerHeight + 'px';
      let a = document.createElement('a');
      a.href = await this.$html2canvas(mapElement, { type: 'dataURL' });
      let imageName = 'image_' + this.image.id.toString() + '_project_' + this.image.project.toString() + '.png';
      a.download = imageName;
      a.click();
      // Reset container css values as previous
      container.style.height = '';
    },
    async fetchLayers() {
      const [userLayersResponse, annotationLayersResponse] = await Promise.all([
        this.project.fetchUserLayers(this.image.id),
        Cytomine.instance.api.get(`image-instances/${this.image.id}/annotation-layers`)
      ]);
      const userLayers = userLayersResponse.array;
      const annotationLayers = annotationLayersResponse.data;
      const layers = userLayers.concat(annotationLayers);
      const idLayers = layers.map(layer => layer.id);
      this.$store.commit(this.imageModule + 'filterSelectedLayers', idLayers);

      if (!this.selectedLayers.length) {
        const currentUserLayer = layers.find(layer => layer.id === this.currentUser.id);
        if (currentUserLayer) {
          this.$store.dispatch(this.imageModule + 'addLayer', {
            ...currentUserLayer,
            visible: true,
            drawOn: this.$store.getters['currentProject/canEditLayer'](currentUserLayer.id)
          });
        }
      }
    },
    publicAssetUrl(path) {
      const base = process.env.BASE_URL || '/';
      return `${base.replace(/\/$/, '')}/${path.replace(/^\//, '')}`;
    },
    applyMockHeatmapOverlay() {
      this.$store.dispatch(this.imageModule + 'setHeatmapOverlay', {
        url: this.publicAssetUrl('heatmap-test.jpg'),
        opacity: 0.55,
        extent: this.extent,
        size: this.imageSize,
        visible: true
      });
    },
  },
  async created() {
    if (!getProj(this.projectionName)) { // if image opened for the first time
      let projection = createProj({ code: this.projectionName, units: 'pixels', extent: this.extent });
      addProj(projection);
    }
    if (this.routedAction === 'review') {
      this.togglePanel('review');
      if (!this.image.inReview) {
        try {
          let clone = await this.image.clone().review();
          this.$store.commit(this.imageModule + 'setImageInstance', clone);
        } catch (error) {
          console.log(error);
          this.$notify({ type: 'error', text: this.$t('notif-error-start-review') });
        }
      }
      this.$store.commit(this.imageModule + 'setReviewMode', true);
    }
    // remove all selected features in order to reselect them when they will be added to the map (otherwise,
    // issue with the select interaction)
    this.selectedLayers.forEach(layer => {
      this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', { layer, cache: true });
    });
    let annot = this.imageWrapper.routedAnnotation;
    if (!annot) {
      let idRoutedAnnot = this.$route.params.idAnnotation;
      if (idRoutedAnnot) {
        try {
          annot = (await Cytomine.instance.api.get(`/annotations/${idRoutedAnnot}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        } catch (error) {
          console.log(error);
          this.$notify({ type: 'error', text: this.$t('notif-error-target-annotation') });
        }
      }
    }
    if (annot) {
      try {
        if (annot.image === this.image.id) {
          if (!this.sliceIds.includes(annot.slice) && Object.prototype.hasOwnProperty.call(annot, 'slice')) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
          }
          this.routedAnnotation = annot;
          if (this.routedAction === 'comments') {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        }
        this.$store.commit(this.imageModule + 'clearRoutedAnnotation');
      } catch (error) {
        console.log(error);
        this.$notify({ type: 'error', text: this.$t('notif-error-target-annotation') });
      }
    }
    // 不阻塞初始化流程，异步保存浏览记录
    new ImageConsultation({ image: this.image.id }).save().catch(error => {
      console.log(error);
      // 这种非关键性错误可以不打扰用户，或者保留通知
      // this.$notify({ type: 'error', text: this.$t('notif-error-save-image-consultation') });
    });

    // 异步加载图层，不阻塞界面渲染
    this.fetchLayers().catch(error => {
      console.log(error);
      this.$notify({ type: 'error', text: this.$t('notif-error-loading-annotation-layers') });
    });
    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('updateMapSize', this.updateMapSize);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$on('close-metadata', this.closeMetadataHandler);
    this.$eventBus.$on('openAddOntologyModal', this.openAddOntologyModalHandler);
    this.setInitialZoom();
    if (this.heatmapMockEnabled) {
      this.applyMockHeatmapOverlay();
    }
  },
  beforeDestroy() {
    this.clearOverviewViewportListeners();
    this.$eventBus.$off('updateMapSize', this.updateMapSize);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$off('close-metadata', this.closeMetadataHandler);
    this.$eventBus.$off('openAddOntologyModal', this.openAddOntologyModalHandler);
  }
};
</script>
<style scoped lang="scss">
@import '../../assets/styles/dark-variables';
@import '../../assets/styles/viewer-layers';

/* ----- Viewer cell ----- */
.map-container {
  position: relative;
  display: flex;
  background:
    radial-gradient(circle at 50% 0%, rgba($primary, 0.035), transparent 34rem),
    $dark-bg-canvas;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.map-container-header {
  position: absolute;
  top: 0.2rem;
  right: 0.2rem;
  z-index: $viewer-z-map-header;

  .close {
    display: block;
    width: 1.65rem;
    height: 1.65rem;
    max-width: none;
    max-height: none;
    min-width: 1.65rem;
    min-height: 1.65rem;
    background-color: rgba(13, 25, 42, 0.86);
    border: 1px solid rgba($primary, 0.2);
    box-shadow: 0 10px 24px rgba(0, 0, 0, 0.26);

    &:hover,
    &:focus {
      background-color: rgba($primary, 0.16);
    }
  }
}

</style>
